import React, { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Clock, AlertCircle, CheckCheck,
  XCircle, MapPin, Calendar, User, Upload, Play,
  Image as ImgIcon, ShieldAlert, Star,
  MessageSquare, Send, Headphones, PhoneCall,
  DollarSign
} from 'lucide-react';
import AppContext from '../../../../../context/AppContext';
import { statusStyle, statusLabels } from '../../../../../components/serviceprovider/workerdashboard/workerbidjobs/bidjobsconstants/BidJobsConstants';
import Stars from '../../workerbidjobs/stars/Stars';
import BidForm from '../../workerbidjobs/bidform/BidForm';

const BidJobCard = ({
  b,
  i,
  jobReviews,
  reviews,
  replies,
  setReplies,
  setLightbox,
  setProofFor,
  setDisputeFor,
  setReviewFor,
  setBidModalFor,
  handleStart,
  handleCancel,
  handleBidSubmit,
  handleReply,
  viewallavailablejobs,
  handleAcceptJob,
  currentWorkerId
}) => {
  const navigate = useNavigate();
  const { baseUrlForFile } = useContext(AppContext);

  // ── Core flags ──
const jobStatus = (b.display_status || b.status || '').toLowerCase();
const isBidding = Number(b.bidding) === 1;

const isNoBidding = Number(b.bidding) === 0;


// meri assigned job hai ya nahi
const isMyAssignedJob =
  Number(b.whserviceprovider_id) === Number(currentWorkerId);
  const myBidObj = Array.isArray(b.whplacebids)
    ? (b.whplacebids.find(
        bid => Number(bid.whserviceprovider_id) === Number(currentWorkerId)
      ) ?? null)
    : null;
    const hasPlacedBid = myBidObj !== null;

const myBidAmount = myBidObj?.bidAmount ?? null;

const myBidMessage = myBidObj?.message ?? null;



const isBidRejected =
  isBidding &&
  hasPlacedBid &&
  b.display_status === "rejected";


const isBidWaiting =
  isBidding &&
  hasPlacedBid &&
  b.display_status === "bid_sent";

  // Kisi aur ko already assign ho gayi → ALREADY ASSIGNED
const isAlreadyAssigned =
    b.already_assigned === true;

  // Kisi ko assign nahi hui → I can accept
const canAcceptNoBid =
    isNoBidding &&
    !isMyAssignedJob &&
    b.whserviceprovider_id == null;

const displayStatus =
  b.display_status || b.status;

  const currentStatus = (displayStatus || 'pending').toLowerCase();

  const st = statusStyle[currentStatus] || statusStyle['bid_sent'] || {
    color: '#64748b',
    bg: 'rgba(100,116,139,0.12)'
  };

  const badgeLabel = currentStatus === 'bid_sent'
    ? 'Bid Sent'
    : statusLabels[currentStatus] || currentStatus;

  const badgeColor = st.color;
  const badgeBg    = st.bg;

  const phone = b.whcustomer?.phoneNumber || b.whcustomer?.phone;
  const showCallIcon = jobStatus === 'assigned' && isMyAssignedJob && !!phone;

  return (
    <div key={b.id} className="aj-card" style={{ animationDelay: i * 0.06 + 's' }}>

      {/* ── Top row ── */}
      <div className="aj-card__top">
        <div>
          <h5 className="aj-card__title">{b.jobTitle}</h5>
          <span className="aj-type-badge aj-type-badge--bid">📋 Bid Job</span>
        </div>
        <div className="aj-card__top-right">
          <span
            className="aj-badge"
            style={{ color: badgeColor, background: badgeBg }}
          >
            {badgeLabel}
          </span>

          {showCallIcon && (
            <a
              href={`tel:${phone}`}
              className="aj-call-icon aj-call-icon--red"
              title="Call Customer"
            >
              <PhoneCall size={18} />
            </a>
          )}
        </div>
      </div>

      {/* ── Meta ── */}
      <div className="aj-card__meta">
        <span><MapPin size={13} /> {b.city}</span>
        <span><MapPin size={13} /> {b.address}</span>
        <span><DollarSign size={13} /> {b.budget}</span>
        <span><Calendar size={13} /> {b.deadline}</span>
        <span><User size={13} /> {b.whcustomer?.fullName}</span>
      </div>

      {/* ── Bid amount row ── */}
      {myBidAmount && (
        <div className="aj-card__bid-row">
          <span className="aj-card__bid-label">Your Bid:</span>
          <span className="aj-card__bid-val">$ {myBidAmount}</span>
          {myBidMessage && <span className="aj-card__msg">"{myBidMessage}"</span>}
        </div>
      )}

      {/* ── Customer media ── */}
      <div className="aj-media-thumbs">
        {b.whcustomerjobmedias?.map((m, mi) => (
          <button
            key={mi}
            className="aj-media-thumb"
            onClick={() =>
              setLightbox({
                ...m,
                type: m.file?.toLowerCase().match(/\.(mp4|mov|webm)$/) ? 'video' : 'image'
              })
            }
          >
            {m.file?.toLowerCase().match(/\.(mp4|mov)$/) ? (
              <>
                <video src={`${baseUrlForFile}/${m.file}`} className="aj-media-thumb__img" />
                <div className="aj-media-thumb__play"><Play size={14} /></div>
              </>
            ) : (
              <img src={`${baseUrlForFile}/${m.file}`} className="aj-media-thumb__img" />
            )}
          </button>
        ))}
      </div>

      {/* ── Proof media ── */}
      {b.proofMedia && b.proofMedia.length > 0 && (
        <div className="aj-media-row">
          <span className="aj-media-label" style={{ color: '#10b981' }}>
            <ImgIcon size={13} /> My Proof:
          </span>
          <div className="aj-media-thumbs">
            {b.proofMedia.map((m, mi) => (
              <button
                key={mi}
                className="aj-media-thumb"
                onClick={() =>
                  setLightbox({
                    ...m,
                    type: m.file?.toLowerCase().match(/\.(mp4|mov|webm)$/) ? 'video' : 'image'
                  })
                }
              >
                {m.type === 'video'
                  ? <video src={`${baseUrlForFile}/storage/${m.file}`} className="aj-media-thumb__img" muted />
                  : <img src={`${baseUrlForFile}/${m.file}`} className="aj-media-thumb__img" />
                }
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── Review ── */}
      {b.whreviews && (
        <div className="aj-review">
          <div className="aj-review__header">
            <User size={14} />
            <span>{b.whcustomer?.fullName}</span>
            <Stars rating={b.whreviews.rating} />
          </div>
          <p className="aj-review__text">{b.whreviews.review}</p>
          {b.whreviews.workerreply ? (
            <div className="aj-review__reply">
              <MessageSquare size={13} />
              <span>Your reply:</span> {b.whreviews.workerreply}
            </div>
          ) : (
            <div className="aj-review__reply-form">
              <input
                type="text"
                placeholder="Write a reply..."
                value={replies[b.whreviews.id] || ''}
                onChange={e =>
                  setReplies(r => ({ ...r, [b.whreviews.id]: e.target.value }))
                }
                className="aj-review__reply-input"
              />
              <button
                className="aj-review__reply-btn"
                onClick={() => handleReply(b.whreviews.id)}
              >
                <Send size={14} />
              </button>
            </div>
          )}
        </div>
      )}

      {/* ══════════════════════════════
           ACTIONS
      ══════════════════════════════ */}
      <div className="aj-card__actions">

        {/* ─────────────────────────────
            BIDDING JOBS
        ───────────────────────────── */}

        {/* Place Bid — sirf tab jab bid nahi ki aur job open/pending hai */}
{isBidding &&
!isMyAssignedJob &&
!hasPlacedBid &&
b.whserviceprovider_id == null &&
(          <button
            className="aj-btn aj-btn--progress"
            onClick={() => setBidModalFor(b.id)}
          >
            <Send size={14} /> Place Bid
          </button>
        )}

      {isBidding && hasPlacedBid && !isMyAssignedJob && (
  <>
    {isBidRejected ? (

      <div className="aj-card__rejected">
        ❌ Your bid was rejected — job assigned to another worker
      </div>

    ) : (

      <>

      <div className="aj-card__bid-success">
        ✅ Your bid submitted successfully
      </div>

      <div className="aj-card__demo-note">
        ⏳ Waiting for customer to accept your bid
      </div>

      </>

    )}
  </>
)}

        {/* ─────────────────────────────
            NO-BIDDING JOBS
        ───────────────────────────── */}

        {/* Accept Job — kisi ne nahi li abhi tak */}
        {canAcceptNoBid && (
          <button
            className="aj-btn aj-btn--progress"
            onClick={() => handleAcceptJob(b.id)}
          >
            <CheckCheck size={14} /> Accept Job
          </button>
        )}

        {/* Already Assigned — kisi aur worker ne le li */}
        {isAlreadyAssigned && (
          <div className="aj-card__demo-note">
            ⚠️ Job already assigned to another worker
          </div>
        )}

        {/* ─────────────────────────────
            MUJHE JOB MILI — assigned
        ───────────────────────────── */}
        { isMyAssignedJob && b.status === 'assigned' && (
          <div className="aj-card__actions-row">
            <div className="aj-card__actions-left">
              <button
                className="aj-btn aj-btn--progress"
                onClick={() => handleStart(b.id)}
              >
                <AlertCircle size={14} /> Start Job
              </button>
              <button
                className="aj-btn aj-btn--reject"
                onClick={() => handleCancel(b.id)}
              >
                <XCircle size={14} /> Cancel
              </button>
            </div>
          </div>
        )}

        {/* in_progress */}
        {isMyAssignedJob && b.status === 'in_progress' && (
          <>
            <button className="aj-btn aj-btn--complete" onClick={() => setProofFor(b.id)}>
              <Upload size={14} /> Upload Proof &amp; Complete
            </button>
            <button className="aj-btn aj-btn--dispute" onClick={() => setDisputeFor(b)}>
              <ShieldAlert size={14} /> Raise Dispute
            </button>
            {b.whcustomer?.phone && (
              <a href={`tel:${b.whcustomer.phone}`} className="aj-btn aj-btn--call">
                <PhoneCall size={14} /> Call Customer
              </a>
            )}
          </>
        )}

        {/* awaiting_confirmation */}
        {isMyAssignedJob && b.display_status === 'awaiting_confirmation'&& (
          <div className="aj-awaiting-wrap">
            <div className="aj-card__awaiting">
              ⏳ Proof submitted — Waiting for customer confirmation (auto-release in 24h)
            </div>
            {b.whcustomer?.phone && (
              <a href={`tel:${b.whcustomer.phone}`} className="aj-btn aj-btn--call">
                <PhoneCall size={14} /> Call Customer
              </a>
            )}
          </div>
        )}

        {/* raise_dispute */}
        {isMyAssignedJob && b.status === 'raise_dispute' && (
          <div className="aj-dispute-row">
            <div
              className="aj-card__dispute"
              style={{ color: '#f97316', background: 'rgba(249,115,22,0.09)', flex: 1 }}
            >
              🛡️ Dispute submitted — chat with Admin to resolve.
            </div>
            <button
              className="aj-btn-dispute-chat"
              onClick={() =>
                navigate(`/worker-chat/${b.whconversation?.id}`, {
                  state: { description: b.description }
                })
              }
            >
              <Headphones size={15} /> Chat
            </button>
          </div>
        )}

        {/* completed */}
        {isMyAssignedJob && b.status === 'completed' && (
          <>
            {!jobReviews[b.id]?.worker?.rating && (
              <button
                className="aj-btn aj-btn--progress"
                onClick={() => setReviewFor(b.id)}
              >
                <Star size={14} /> Rate &amp; Review
              </button>
            )}

            {jobReviews[b.id]?.customer?.rating && (
              <div className="aj-review-display aj-review-display--customer">
                <div className="aj-review-display__header">
                  <span className="aj-review-badge aj-review-badge--customer">Customer Review</span>
                  <div className="aj-stars">
                    {[1, 2, 3, 4, 5].map(s => (
                      <Star
                        key={s} size={13}
                        fill={s <= jobReviews[b.id].customer.rating ? '#f59e0b' : '#e5e7eb'}
                        color={s <= jobReviews[b.id].customer.rating ? '#f59e0b' : '#e5e7eb'}
                      />
                    ))}
                  </div>
                </div>
                <p className="aj-review-display__text">{jobReviews[b.id].customer.review}</p>
              </div>
            )}

            {jobReviews[b.id]?.worker?.rating && (
              <div className="aj-review-display aj-review-display--worker">
                <div className="aj-review-display__header">
                  <span className="aj-review-badge aj-review-badge--worker">Your Review</span>
                  <div className="aj-stars">
                    {[1, 2, 3, 4, 5].map(s => (
                      <Star
                        key={s} size={13}
                        fill={s <= jobReviews[b.id].worker.rating ? '#f59e0b' : '#e5e7eb'}
                        color={s <= jobReviews[b.id].worker.rating ? '#f59e0b' : '#e5e7eb'}
                      />
                    ))}
                  </div>
                </div>
                <p className="aj-review-display__text">{jobReviews[b.id].worker.review}</p>
              </div>
            )}
          </>
        )}

        {/* ─────────────────────────────
            STATIC STATUS MESSAGES
        ───────────────────────────── */}
        {b.display_status === 'cancelled'     && <div className="aj-card__rejected">❌ Bid was not accepted or job cancelled</div>}
        {b.display_status === 'refunded'       && <div className="aj-card__rejected">💸 Refunded to customer</div>}
        {b.display_status === 'dispute_frozen' && <div className="aj-card__dispute">🔒 Under admin review — all actions paused</div>}
        {b.display_status === 'admin_review'   && <div className="aj-card__dispute">⚠️ Escalated for manual decision</div>}

      </div>
    </div>
  );
};

export default BidJobCard;